import styled from 'styled-components'

export const Wrapper = styled.div<{ $maximized?: boolean }>`
  width: 100%;
  flex: 1;
  min-height: 400px;
  height: 100%;
  background: #0f1218;
  color: #ffffff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${({ $maximized }) =>
    $maximized
      ? `
    position: fixed;
    inset: 0;
    z-index: 10000;
    flex: none;
    width: auto;
    height: auto;
    min-height: 0;
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
    padding-top: env(safe-area-inset-top, 0px);
    padding-right: env(safe-area-inset-right, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    padding-left: env(safe-area-inset-left, 0px);
    box-sizing: border-box;
  `
      : ''}
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  background: #171b24;
  border-bottom: 1px solid #232936;
`

export const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
`

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: minmax(0, 1fr);
  position: relative;
`

export const MainGrid = styled.div<{
  /** One patient tile or “waiting” — fill width and height of the stage */
  fillsMainStage?: boolean
  fullscreenMultiRemote?: boolean
}>`
  display: grid;
  gap: 12px;
  padding: 12px;
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;

  ${({ fillsMainStage }) =>
    fillsMainStage
      ? `
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr);
    align-content: stretch;
    justify-content: stretch;
    & > * {
      width: 100%;
      height: 100%;
      min-height: 0;
      align-self: stretch;
    }
  `
      : ''}

  ${({ fillsMainStage, fullscreenMultiRemote }) =>
    !fillsMainStage && fullscreenMultiRemote
      ? `
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    grid-auto-rows: minmax(220px, 1fr);
    align-content: stretch;
  `
      : ''}

  ${({ fillsMainStage, fullscreenMultiRemote }) =>
    !fillsMainStage && !fullscreenMultiRemote
      ? `
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    align-content: start;
  `
      : ''}
`

export const LayoutStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
`

export const Tile = styled.div<{ small?: boolean }>`
  background: #05070d;
  border: 1px solid #2a3040;
  border-radius: 12px;
  min-height: ${({ small }) => (small ? '140px' : '220px')};
  position: relative;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const LocalFloatingTile = styled(Tile)`
  position: absolute;
  right: 16px;
  bottom: 16px;
  width: 230px;
  min-height: 130px;
  height: 130px;
  z-index: 5;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);

  @media (max-width: 720px) {
    width: 150px;
    height: 90px;
    min-height: 60px;
  }
  @media (max-width: 480px) {
    width: 100px;
    height: 60px;
    min-height: 60px;
  }
`

export const TileLabel = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
`

export const LocalVideoOffOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(5, 7, 13, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  z-index: 3;
`

export const EmptyRemotePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  border: 1px dashed #2a3040;
  border-radius: 12px;
  background: rgba(10, 14, 24, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #c6cedf;
  font-size: 14px;
  font-weight: 600;
  padding: 16px;
  box-sizing: border-box;
`

export const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #232936;
  background: #171b24;
  flex-wrap: wrap;
`

export const ControlButton = styled.button<{
  danger?: boolean
  active?: boolean
}>`
  border: 0;
  border-radius: 8px;
  width: 50px;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  background: ${({ danger, active }) => {
    if (danger) return '#b42318'
    if (active) return '#1e8b55'
    return '#2c3446'
  }};
  color: #fff;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(1.08);
  }
`

export const NetworkBadge = styled.div<{ status: string }>`
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ status }) => {
    if (status === 'excellent') return '#1f9254'
    if (status === 'good') return '#1e8b55'
    if (status === 'poor') return '#b96f15'
    if (status === 'critical') return '#c83e2d'
    return '#6b7280'
  }};
`

export const SidePanel = styled.div`
  position: absolute;
  right: 12px;
  top: 12px;
  width: 280px;
  border-radius: 12px;
  background: #161c27;
  border: 1px solid #2a3040;
  padding: 12px;
  z-index: 9;
  @media (max-width: 480px) {
    right: 0;
    top: 0;
    bottom: 0;
    width: auto;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }
`

export const SectionTitle = styled.h4`
  font-size: 14px;
  margin: 0 0 10px;
`

export const OptionButton = styled.button<{ selected?: boolean }>`
  width: 100%;
  border: 1px solid ${({ selected }) => (selected ? '#53b486' : '#2c3446')};
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 8px;
  background: ${({ selected }) => (selected ? '#1e8b55' : '#1f2533')};
  color: #fff;
  text-align: left;
  cursor: pointer;
`

export const CloseButton = styled(OptionButton)`
  background: #f3ecff;
  color: #c83e2d;
  font-weight: 700;
  text-align: center;
`

export const Select = styled.select`
  width: 100%;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #2c3446;
  margin-bottom: 10px;
  padding: 0 10px;
  background: #1f2533;
  color: #fff;
`

export const ErrorMessage = styled.div`
  background: #5f1b16;
  color: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 10px 12px 0;
  font-size: 12px;
`

export const RemoteStatusNotice = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: rgba(13, 17, 25, 0.92);
  border: 1px solid #2a3040;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
`
